/**
 * What counts as a gap in a mould record.
 *
 * One definition, three readers: the Data Quality page lists them, the
 * dashboard counts them, and the catalog filters by them. They used to live
 * only inside the Data Quality page, which was fine while it was the only
 * screen that cared - the moment a dashboard number claims "20 unassigned" and
 * a filtered list has to show exactly those twenty, a second copy of the test
 * is a promise the two screens will eventually disagree.
 *
 * Severity is about consequence, not tidiness:
 *   - `blocker`  - engraved on the nameplate: its label is engraved with nothing
 *                  after it while blank (every mould can be engraved; the plate is
 *                  just less complete)
 *   - `warning`  - reports will be wrong or incomplete
 *   - `info`     - worth filling in eventually
 */
import type { MouldRow } from '../../db/queries.js';

export type Severity = 'blocker' | 'warning' | 'info';

export interface Rule {
  id: string;
  label: string;
  /** Short form for a dashboard tile, where the row is already labelled. */
  short: string;
  severity: Severity;
  why: string;
  /** True when this mould *fails* the rule. */
  test: (m: MouldRow) => boolean;
}

export const RULES: Rule[] = [
  {
    id: 'name',
    label: 'Missing mould name',
    short: 'No name',
    severity: 'blocker',
    why: 'Engraved as "Mold Name :-" on the nameplate.',
    test: (m) => !m.name?.trim(),
  },
  {
    id: 'cavities',
    label: 'Missing cavity count',
    short: 'No cavity count',
    severity: 'blocker',
    why: 'Engraved as "Cavity:" on the nameplate.',
    test: (m) => !m.cavities?.trim(),
  },
  {
    id: 'runner',
    label: 'Missing runner type',
    short: 'No runner type',
    severity: 'warning',
    why: 'Part of the specification. (Engraved only on Design A, beside the cavity count.)',
    test: (m) => !m.runnerTypeName,
  },
  {
    id: 'size',
    label: 'Missing mould size',
    short: 'No mould size',
    severity: 'blocker',
    why: 'Engraved as "Mold Dimension:" on the nameplate.',
    test: (m) => !m.moldSize?.trim(),
  },
  {
    id: 'manufactureYear',
    label: 'Missing manufacturing year',
    short: 'No manufacturing year',
    severity: 'warning',
    why: 'Part of the record. Engraved only on Design A plates ("MANUFACTURING YEAR :-"), not on Design B.',
    test: (m) => !m.manufactureYear,
  },
  {
    id: 'owner',
    label: 'No owner company',
    short: 'Unassigned',
    severity: 'warning',
    why: 'Engraved as "Property Ownership:", and ownership reports depend on it.',
    test: (m) => !m.ownerCompanyName,
  },
  {
    id: 'project',
    label: 'No project',
    short: 'No project',
    severity: 'warning',
    why: 'Mould IDs are allocated from the project code.',
    test: (m) => !m.projectName,
  },
  {
    id: 'toolType',
    label: 'No tool type',
    short: 'No tool type',
    severity: 'warning',
    why: 'Needed to group the catalog by tool family.',
    test: (m) => !m.toolTypeName,
  },
  {
    id: 'template',
    label: 'No nameplate design chosen',
    short: 'Default plate',
    severity: 'info',
    why: 'Engraved with Design B, the default, until another design is chosen.',
    test: (m) => !m.nameplateTemplateName,
  },
  {
    id: 'photo',
    label: 'No photo',
    short: 'No photo',
    severity: 'info',
    why: 'Photos make the mould identifiable on a phone in the store.',
    test: (m) => !m.photoUrl,
  },
  {
    id: 'materialGrade',
    label: 'No moulding material / shrinkage',
    short: 'No material',
    severity: 'info',
    why: 'Needed for process setup, not for the plate.',
    test: (m) => !m.materialGradeName,
  },
  {
    id: 'designer',
    label: 'No designer',
    short: 'No designer',
    severity: 'info',
    why: 'Useful for traceability on rework.',
    test: (m) => !m.designerName,
  },
];

export const RULE_BY_ID: Record<string, Rule> = Object.fromEntries(
  RULES.map((r) => [r.id, r]),
);

/** Count how many moulds fail each rule. */
export function countGaps(rows: MouldRow[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const rule of RULES) counts[rule.id] = 0;
  for (const m of rows) {
    for (const rule of RULES) if (rule.test(m)) counts[rule.id]! += 1;
  }
  return counts;
}

/**
 * The gaps worth putting on a dashboard, in the order they are worth fixing.
 *
 * Not all twelve: a dashboard card that lists every rule is a report, and a
 * report is the thing somebody has to decide to go and read. These are the ones
 * where the fix is quick and the consequence is felt.
 */
export const HEADLINE_GAPS = ['owner', 'project', 'photo', 'size', 'cavities'] as const;
